import { Button } from '@/components/ui/Button'
import { formatCurrency } from '@/utils/format-currency'
import type { Partner } from '@/types'

function formatCommissionReference(competencia: string | null | undefined, dataReferencia: string) {
  if (competencia === 'UNICA') return 'Pagamento único'
  if (competencia && /^P\d+$/.test(competencia)) {
    return `Parcela ${competencia.slice(1)} · ${new Date(dataReferencia).toLocaleDateString('pt-BR')}`
  }
  if (competencia && /^\d{4}-\d{2}$/.test(competencia)) {
    const [year, month] = competencia.split('-')
    return `Competência ${month}/${year}`
  }
  return new Date(dataReferencia).toLocaleDateString('pt-BR')
}

interface PartnerDetailFinanceTabProps {
  partner: Partner
  onUpdateCommissionStatus?: (commissionId: string, status: string) => void
  onOpenPaymentModal: () => void
}

export function PartnerDetailFinanceTab({
  partner,
  onUpdateCommissionStatus,
  onOpenPaymentModal,
}: PartnerDetailFinanceTabProps) {
  const pendingCommissions = (partner.comissoes ?? []).filter((c) => c.status !== 'paga')

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h4 className='text-[var(--font-color-label)] uppercase tracking-wider font-medium' style={{ fontSize: 'var(--font-size-label)' }}>
          Gestão Financeira
        </h4>
        <Button variant='primary' size='sm' onClick={onOpenPaymentModal}>
          Registrar Pagamento
        </Button>
      </div>

      <div className='grid grid-cols-2 gap-4'>
        <div className='glass-card p-0 overflow-hidden'>
          <div className='px-4 py-3 border-b border-[var(--glass-border)]'>
            <p className='text-[var(--font-color-label)] uppercase tracking-wider font-medium' style={{ fontSize: 'var(--font-size-label)' }}>
              Próximos Pagamentos
            </p>
          </div>
          <div className='divide-y divide-[var(--glass-border)]'>
            {pendingCommissions.map((c) => (
              <div key={c.id} className='px-4 py-3 flex items-center justify-between hover:bg-[var(--glass-bg)] transition-colors'>
                <div className='min-w-0'>
                  <p className='text-[var(--font-color-muted)] text-[11px]'>
                    {formatCommissionReference(c.competencia, c.dataReferencia)}
                    {c.nivel != null && (
                      <span className='ml-1.5 rounded bg-[var(--glass-bg)] px-1 py-px text-[9px] font-bold text-[var(--text-muted)]'>
                        N{c.nivel}
                      </span>
                    )}
                  </p>
                  <p className='font-bold text-sm text-[var(--font-color-body)]'>
                    {formatCurrency(Number(c.valor))}
                  </p>
                  {(c.deal || (c.nivel != null && c.nivel > 1 && c.parceiroOrigem)) && (
                    <p className='truncate text-[10px] text-[var(--font-color-muted)]'>
                      {c.deal?.nome}
                      {c.nivel != null && c.nivel > 1 && c.parceiroOrigem && ` · via ${c.parceiroOrigem.nome}`}
                    </p>
                  )}
                </div>
                <select
                  className='bg-transparent text-[11px] font-bold uppercase text-[var(--text-muted)] border-none outline-none cursor-pointer'
                  value={c.status}
                  onChange={(e) => onUpdateCommissionStatus?.(c.id, e.target.value)}
                >
                  <option value='pendente'>Pendente</option>
                  <option value='aprovada'>Aprovada</option>
                  <option value='paga'>Paga</option>
                </select>
              </div>
            ))}
            {pendingCommissions.length === 0 && (
              <div className='px-4 py-6 text-center text-[var(--font-color-muted)] text-sm'>
                Nenhum pagamento pendente
              </div>
            )}
          </div>
        </div>

        <div className='glass-card p-0 overflow-hidden'>
          <div className='px-4 py-3 border-b border-[var(--glass-border)]'>
            <p className='text-[var(--font-color-label)] uppercase tracking-wider font-medium' style={{ fontSize: 'var(--font-size-label)' }}>
              Histórico de Recebimentos
            </p>
          </div>
          <div className='divide-y divide-[var(--glass-border)]'>
            {(partner.pagamentos ?? []).map((p) => (
              <div key={p.id} className='px-4 py-3 flex items-center justify-between hover:bg-[var(--glass-bg)] transition-colors'>
                <div>
                  <p className='text-[var(--font-color-muted)] text-[11px]'>
                    {new Date(p.dataPagamento).toLocaleDateString('pt-BR')}
                  </p>
                  <p className='font-bold text-sm text-emerald-500'>
                    {formatCurrency(Number(p.valor))}
                  </p>
                </div>
                <span className='px-2 py-0.5 rounded-md text-[9px] font-bold bg-[var(--glass-bg)] text-[var(--text-muted)]'>
                  {p.metodo}
                </span>
              </div>
            ))}
            {(!partner.pagamentos || partner.pagamentos.length === 0) && (
              <div className='px-4 py-6 text-center text-[var(--font-color-muted)] text-sm'>
                Nenhum recebimento registrado
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
